import { useExerciseState } from "@/contexts/exercise/Context";
import {
  useSaveExerciseProgressMutation,
  useUpdateExerciseStatusMutation,
} from "@/data/exercises-v2";
import {
  NewResult,
  Submission,
  RequestData,
  ExerciseOptionTypes,
} from "@/types";

export function useExerciseHandlers() {
  const { mutate: saveProgress } = useSaveExerciseProgressMutation();
  const { mutate: updateExerciseStatus } = useUpdateExerciseStatusMutation();
  const { startTime, timerDuration, exerciseId } = useExerciseState();

  const calculateRemainingTime = () => {
    const currentTime = Date.now();

    if (startTime && timerDuration) {
      const elapsedSeconds = (currentTime - startTime) / 1000;

      return Math.max(Math.floor(timerDuration - elapsedSeconds), 0);
    }

    return 0;
  };

  const handleSaveProgress = async (
    submissionData: Submission[],
    exerciseType: ExerciseOptionTypes,
    newResult: NewResult,
    averageScore: number,
  ) => {
    const remainingTime = calculateRemainingTime();

    const requestData: RequestData[] = submissionData.map((submission) => {
      const array = [ExerciseOptionTypes.GapFill, ExerciseOptionTypes.Grouping];

      const optionId = !array.includes(exerciseType)
        ? submission.selectedOptionId
        : undefined;

      const textAnswer = array.includes(exerciseType)
        ? submission.textAnswer
        : [
            ExerciseOptionTypes.MultipleChoice,
            ExerciseOptionTypes.TrueFalse,
            ExerciseOptionTypes.MatchingOption,
          ].includes(exerciseType)
        ? Number(Boolean(submission?.isCorrect)).toString()
        : undefined;

      return {
        option_id: optionId,
        text_answer: textAnswer,
        question_id: submission.questionId,
        current_time: remainingTime,
        exercise_type: exerciseType,
        course_exercise_id: exerciseId,
      };
    });

    const payload = {
      exercises: requestData,
      average_score: averageScore,
      exercise_results: newResult,
    };

    try {
      saveProgress(payload);
    } catch (error) {
      console.error("Error saving submissions:", error);
    }
  };

  const handleUpdateExerciseStatus = async (status: string) => {
    const requestData: RequestData = {
      status: status,
      course_exercise_id: exerciseId,
    };

    try {
      updateExerciseStatus(requestData);
    } catch (error) {
      console.error(
        "Errore nell'aggiornamento dello stato dell'esercizio:",
        error,
      );
    }
  };

  return { handleSaveProgress, handleUpdateExerciseStatus };
}
